import { useMemo } from 'react';
import { formatMessage, insertPlaceholder } from '../domain/compose';
import { composeLimits } from '../domain/notifications';
import { insertNoteImage } from '../domain/resources';

// The edits a message box's toolbar makes (#596, #604, #531), for every box
// that is MMS's message editor: New Message's (useNewMessage.js) and a Custom
// Email Template's (#608, CustomTemplateDrawer.jsx). Each is the core's — the
// toolbar's Markdown, the placeholder menu's token, Insert Image's picture —
// over the box's selection, and each answers with the selection to put back
// (`{ start, end }`, which MessageBodyEditor.jsx does once React has the new
// text) or null when the core refused it.
//
// `apply(patch)` is the owner's: `{ body }` for the new text, or `{ notice }`
// for the sentence that says why nothing changed.
//
// `insert` is the placeholder menu's edit, New Message's by default; the
// Send Invoice drawer's menu (#622) has tokens of its own and hands in
// `invoiceEmailInsertPlaceholder` (src/domain/invoiceEmail.js).
export default function useMessageBodyEdits(body, apply, { insert = insertPlaceholder } = {}) {
  return useMemo(() => {
    const max = composeLimits().body;
    const done = (r, selection) => {
      if (r.refusal) {
        apply({ notice: r.refusal.message });
        return null;
      }
      apply({ body: r.value });
      return selection;
    };
    return {
      // A toolbar button: what it wrapped stays selected.
      format: (name, start, end) => {
        const r = formatMessage(body, start, end, name, max);
        return done(r, { start: r.start, end: r.end });
      },
      // The placeholder menu: its token in place of the selection, the
      // cursor after it.
      insertPlaceholder: (key, start, end) => {
        const r = insert(body, start, end, key, max);
        return done(r, { start: r.start, end: r.start });
      },
      // Insert Image: a library picture on a line of its own, by its id, the
      // cursor after it.
      insertImage: (row, start, end) => {
        const r = insertNoteImage(body, start, end, row);
        return done(r, { start: r.cursor, end: r.cursor });
      },
    };
  }, [body, apply, insert]);
}
